<template>
  <div class="guides-quotes">
    <IconBase
      name="quote-icon"
      size="32"
    />
    <div class="guides-quotes-list">
      <GuideQuotesQuote
        v-for="quote in guidesQuotes"
        :key="quote.id"
        v-bind="quote"
        @guide-clicked="handleGuideClick"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, inject } from 'vue';

  import type { ArticleContent } from '@/features/articleContent';
  import type { ApiGuide } from '@/features/articleContent/lib/articleComponents';
  import { mapApiGuideForQuotes } from '@/features/articleContent/lib/mapApiGuideForBlocks';
  import GuideQuotesQuote, { type Props as GuideQuote } from './Quote.vue';
  import { IconBase } from '@/shared/ui/iconBase';

  export type Guide = Omit<GuideQuote, 'title' | 'text'>;

  /** Строка — старый формат, массив — новый (tip-tap) */
  interface QuoteFromApi {
    guide_id: number;
    title?: ArticleContent | string;
    text?: ArticleContent | string;
  }

  interface Props {
    quotes?: QuoteFromApi[];
    postGuides?: ApiGuide[];
  }

  const props = defineProps<Props>();
  const sendGuideClickEvent = inject<((guideId: number, clickType: string) => void) | undefined>('sendGuideClickEvent');

  const handleGuideClick = (guideId: number) => {
    if (sendGuideClickEvent) {
      sendGuideClickEvent(guideId, 'comment');
    }
  };

  const emptyQuoteGuide = (guideId: number): Guide => ({
    id: guideId,
    name: '',
    avatarLink: '',
    location: '',
    experienceCount: 0,
  });

  const guidesQuotes = computed(() => {
    const { quotes, postGuides } = props;
    if (!quotes?.length) {
      return [];
    }

    const postList = Array.isArray(postGuides) ? postGuides : [];
    const guidesById = new Map(postList.map((api) => [Number(api.id), mapApiGuideForQuotes(api)]));
    const result: GuideQuote[] = [];

    for (const quote of quotes) {
      const guideId = quote.guide_id;
      const title: ArticleContent = Array.isArray(quote.title) ? quote.title : [];
      const text: ArticleContent = Array.isArray(quote.text) ? quote.text : [];
      if (!guideId || !title.length || !text.length) {
        continue;
      }

      const base = guidesById.get(guideId) ?? emptyQuoteGuide(guideId);
      result.push({ ...base, title, text });
    }

    return result;
  });
</script>

<style lang="scss" scoped>
  .guides-quotes {
    display: flex;
    gap: var(--alias-space-20);
    align-items: flex-start;
    max-width: var(--width-content);
    margin-top: var(--alias-space-32);

    @include tablet {
      gap: var(--alias-space-16);
      margin-top: var(--alias-space-24);
    }
  }

  .guides-quotes-list {
    display: flex;
    flex-direction: column;
    gap: var(--alias-space-24);
  }
</style>
